import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { PageHeader } from '../components/common/PageHeader';
import { SearchInput } from '../components/common/SearchInput';
import { Button } from '../components/common/Button';
import { Icon } from '../components/common/Icon';
import { ProductCard } from '../components/product/ProductCard';
import { FilterPanel } from '../components/product/FilterPanel';
import { emptyFilters, activeFilterCount, type FiltersState, type FilterGroup } from '../components/product/filterState';
import { MobileFilterDrawer } from '../components/product/MobileFilterDrawer';
import { productRepository, type FilterFacets } from '../services/productRepository';
import { captureFlip, runFlip, type FlipState } from '../anim/galleryFlip';
import { useReducedMotion } from '../anim/useReducedMotion';
import { breadcrumbSchema, collectionPageSchema, itemListSchema } from '../lib/seo';
import { useProducts, useFilterFacets } from '../hooks';

const EMPTY_FACETS: FilterFacets = { categories: [], applications: [], benefits: [], crops: [] };

export default function ProductsPage() {
  const { data: facets = EMPTY_FACETS } = useFilterFacets();
  const { data: products = [], isLoading: loading } = useProducts();
  // ?q= is a real, addressable search. The WebSite structured data advertises
  // /catalog?q={search_term_string} as this site's search entry point, so that url has to actually
  // search — and a shared or bookmarked search has to come back the same.
  const [params, setParams] = useSearchParams();
  const urlQuery = params.get('q') ?? '';
  const [filters, setFilters] = useState<FiltersState>({ ...emptyFilters, query: urlQuery });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const reduced = useReducedMotion();

  // Back/forward and an externally supplied ?q= both land here.
  useEffect(() => {
    setFilters((prev) => (prev.query === urlQuery ? prev : { ...prev, query: urlQuery }));
  }, [urlQuery]);

  const setQuery = (q: string) => {
    setFilters((prev) => ({ ...prev, query: q }));
    const next = new URLSearchParams(params);
    if (q.trim()) next.set('q', q);
    else next.delete('q');
    // replace, not push: typing must not fill the history stack with one entry per keystroke.
    setParams(next, { replace: true });
  };

  const gridRef = useRef<HTMLDivElement>(null);
  const flipRef = useRef<FlipState | null>(null);

  // filterSync reads the repo cache — recompute when the loaded products change too, not just filters.
  const filtered = useMemo(() => productRepository.filterSync(filters), [filters, products]);
  const visibleKey = filtered.map((p) => p.slug).join(',');

  const beginFlip = () => {
    if (reduced || !gridRef.current) return;
    flipRef.current = captureFlip(Array.from(gridRef.current.querySelectorAll('[data-flip-id]')));
  };

  useLayoutEffect(() => {
    if (flipRef.current) {
      runFlip(flipRef.current);
      flipRef.current = null;
    }
  }, [visibleKey]);

  const toggle = (group: FilterGroup, value: string) => {
    beginFlip();
    setFilters((prev) => {
      const set = new Set(prev[group]);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return { ...prev, [group]: [...set] };
    });
  };
  const clearAll = () => {
    beginFlip();
    setFilters(emptyFilters);
    const next = new URLSearchParams(params);
    next.delete('q');
    setParams(next, { replace: true });
  };

  const count = activeFilterCount(filters);

  return (
    <>
      <Seo
        title="All Products — Bio-Fertilizers, Biostimulants & Plant Strength"
        description="Explore the full GreenbayBio range: bio-fertilizers, soil improvers, biostimulants, plant-strength foliars and water management — with usage, composition and pack sizes."
        path="/catalog"
        keywords={['GreenbayBio products', 'bio fertilizer products', 'biostimulant range India']}
        jsonLd={[
          collectionPageSchema({
            name: 'GreenbayBio product range',
            description:
              'The full GreenbayBio range of bio-fertilizers, soil improvers, biostimulants, plant-strength foliars and water-management products.',
            path: '/catalog',
          }),
          // Built from the LOADED catalogue, not the filtered view — the page's structured data
          // describes the range, not whatever the visitor has narrowed it to.
          itemListSchema(
            'GreenbayBio products',
            '/catalog',
            products.map((p) => ({ name: p.name, path: `/catalog/${p.slug}` })),
          ),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Products', path: '/catalog' }]),
        ]}
      />
      <PageHeader
        eyebrow="The range"
        title="Products for living soil and stronger crops."
        subtitle="Bio-based products across five categories — each with full usage, composition, dosage and pack-size details."
      >
        <SearchInput
          value={filters.query}
          onChange={setQuery}
          placeholder="Search products, crops or benefits…"
          tone="dark"
          className="max-w-xl"
        />
      </PageHeader>

      <div className="container-x grid gap-10 py-14 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block">
          <div className="rounded-xl border border-sand-200 bg-white p-6 shadow-soft">
            <FilterPanel facets={facets} state={filters} onToggle={toggle} onClearAll={clearAll} />
          </div>
        </aside>

        <div>
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-ink-500" aria-live="polite">
              <span className="font-semibold text-ink">{filtered.length}</span> {filtered.length === 1 ? 'product' : 'products'}
              {count > 0 && ' matched'}
            </p>
            <Button variant="outline" size="sm" className="lg:hidden" onClick={() => setDrawerOpen(true)}>
              <Icon name="SlidersHorizontal" className="h-4 w-4" /> Filters {count > 0 && `(${count})`}
            </Button>
          </div>

          {count > 0 && (
            <div className="mb-6 flex flex-wrap items-center gap-2">
              {(['categories', 'benefits', 'applications', 'crops'] as FilterGroup[]).flatMap((g) =>
                filters[g].map((v) => {
                  const label = g === 'categories' ? facets.categories.find((c) => c.slug === v)?.name ?? v : v;
                  return (
                    <button
                      key={`${g}-${v}`}
                      onClick={() => toggle(g, v)}
                      className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-sm text-brand-800 hover:bg-brand-100"
                    >
                      {label}
                      <Icon name="X" className="h-3.5 w-3.5" />
                    </button>
                  );
                }),
              )}
              <button onClick={clearAll} className="text-sm font-medium text-ink-500 underline hover:text-brand-600">
                Clear all
              </button>
            </div>
          )}

          {loading ? (
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse rounded-lg border border-sand-200 bg-white p-6">
                  <div className="aspect-[4/3.4] rounded-lg bg-sand-100" />
                  <div className="mt-5 h-5 w-1/2 rounded bg-sand-100" />
                  <div className="mt-3 h-4 w-3/4 rounded bg-sand-100" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center rounded-xl border border-dashed border-sand-200 bg-white py-20 text-center">
              <Icon name="Search" className="h-10 w-10 text-ink-300" />
              <h2 className="mt-4 font-display text-xl font-semibold text-ink">No products match those filters</h2>
              <p className="mt-2 max-w-sm text-ink-500">Try removing a filter or searching for a different crop or benefit.</p>
              <Button variant="primary" size="md" className="mt-6" onClick={clearAll}>
                Clear all filters
              </Button>
            </div>
          ) : (
            <div ref={gridRef} className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {filtered.map((p, i) => (
                <ProductCard key={p.slug} product={p} priority={i < 3} />
              ))}
            </div>
          )}
        </div>
      </div>

      <MobileFilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        facets={facets}
        state={filters}
        onToggle={toggle}
        onClearAll={clearAll}
        resultCount={filtered.length}
      />
    </>
  );
}
